import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  TablePagination,
  TablePaginationProps,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { AtonUnitDetails, Fault } from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import PaddedBox from "~/components/PaddedBox";

type ConditionalProps =
  | {
      atonData: AtonUnitDetails;
      faultsData?: never;
    }
  | {
      faultsData: Fault[];
      atonData?: never;
    };

type Props = { small?: boolean } & ConditionalProps;

const AtoNFaultsTab = ({ atonData, small, faultsData }: Props): JSX.Element => {
  const { t } = useTranslation("aton-fault");

  const theme = useTheme();

  const faultValues = atonData?.faults ?? faultsData;

  const columns: CustomColumn<Fault>[] = React.useMemo(
    () => [
      {
        title: t("type"),
        field: "type",
      },
      {
        title: t("state"),
        field: "state",
      },
      {
        title: t("notFunctional"),
        field: "atonUnitNonfunctional",
        render: ({ atonUnitNonfunctional }) =>
          !atonUnitNonfunctional ? <CheckIcon /> : <CloseIcon />,
      },
      {
        title: t("aton-action:atonUnit"),
        field: "atonUnit",
        render: ({ atonUnit }) => {
          if (!atonUnit) return "";
          const { type, atonNumber, name } = atonUnit;
          return (
            <Box>
              <Typography>{`${atonNumber} ${name}`}</Typography>
              <Typography>{type}</Typography>
            </Box>
          );
        },
      },
      {
        title: t("observedDate"),
        field: "observationTimestamp",
        render: ({ observationTimestamp }) =>
          observationTimestamp
            ? dayjs(observationTimestamp).format("DD/MM/YYYY HH:mm:ss")
            : "",
      },
      {
        title: t("recordDate"),
        field: "entryTimestamp",
        render: ({ entryTimestamp }) =>
          entryTimestamp
            ? dayjs(entryTimestamp).format("DD/MM/YYYY HH:mm:ss")
            : "",
      },
      {
        title: t("fixedTimestamp"),
        field: "fixedTimestamp",
        render: ({ fixedTimestamp }) =>
          fixedTimestamp
            ? dayjs(fixedTimestamp).format("DD/MM/YYYY HH:mm:ss")
            : "",
      },
      {
        title: t("observer"),
        field: "observerName",
      },
      {
        title: t("contact"),
        field: "contact",
      },
      {
        title: t("information"),
        field: "memo",
      },
    ],
    [t],
  );

  const tableProps: CustomTableProps<Fault> = React.useMemo(
    () => ({
      title: t("aton:list-faults"),
      ...(small
        ? {
            options: {
              pageSizeOptions: [5, 10, 15, 25, 50],
              pageSize: 10,
            },
            components: {
              Container: (props) => (
                <div
                  {...props}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    border: "none",
                  }}
                />
              ),
              Pagination: (props: TablePaginationProps) => {
                const { style } = props;
                return (
                  <TablePagination
                    {...props}
                    style={{
                      borderBottomRightRadius: theme.shape.borderRadius,
                      ...style,
                    }}
                  />
                );
              },
            },
          }
        : {}),
    }),
    [small, t, theme.shape.borderRadius],
  );

  const table = React.useMemo(
    () => (
      <GenericTable
        data={faultValues ?? []}
        columns={columns}
        tableProps={tableProps}
      />
    ),
    [columns, faultValues, tableProps],
  );

  return small ? table : <PaddedBox>{table}</PaddedBox>;
};

export default AtoNFaultsTab;
